import { useQuery } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users,
  TrendingUp,
  AlertTriangle,
  Building2,
  Activity,
  ArrowRight,
  Clock,
  CheckCircle,
  XCircle,
  Minus,
} from "lucide-react";
import { Link } from "wouter";
import { useGaContext } from "@/hooks/useGaContext";

function AdoptionBadge({ band }: { band: string }) {
  const colors: Record<string, string> = {
    building: "bg-muted text-muted-foreground",
    active: "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300",
    power_user: "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300",
  };
  const labels: Record<string, string> = {
    building: "Building",
    active: "Active",
    power_user: "Power User",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[band] || "bg-muted text-muted-foreground"}`}>
      {labels[band] || band}
    </span>
  );
}

function AlertChip({ alert }: { alert: any }) {
  const isRed = alert.severity === "red";
  return (
    <Link href={`/ga/brokers/${alert.brokerAccountId}`}>
      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap shrink-0 ${
        isRed ? "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800" : "bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
      }`}>
        <AlertTriangle className="w-3 h-3" />
        {alert.label}
      </div>
    </Link>
  );
}

export default function GADashboard() {
  const { gaId } = useGaContext();

  const { data: network } = useQuery<any>({
    queryKey: ["/api/ga", gaId, "analytics", "network"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/analytics/network`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!gaId,
  });

  const { data: alerts = [] } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "analytics", "alerts"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/analytics/alerts`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const { data: brokers = [] } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "analytics", "brokers"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/analytics/brokers`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const { data: recentActivity = [] } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "audit-log"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/audit-log?limit=10`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const snap = network?.latest;
  const totalLicenses = snap?.licensesAssigned + snap?.licensesAvailable || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Network Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-0.5">GA partner network overview</p>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Brokers</p>
                <p className="text-3xl font-bold text-foreground mt-1">{snap?.totalBrokers ?? 0}</p>
              </div>
              <Users className="w-5 h-5 text-muted-foreground mt-1" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Active (30d)</p>
                <p className="text-3xl font-bold text-primary mt-1">{snap?.brokersActive30d ?? 0}</p>
              </div>
              <Activity className="w-5 h-5 text-blue-400 dark:text-blue-500 mt-1" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Avg Adoption Score</p>
                <p className="text-3xl font-bold text-foreground mt-1">{snap?.avgAdoptionScore ?? 0}</p>
              </div>
              <TrendingUp className="w-5 h-5 text-green-400 dark:text-green-500 mt-1" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Network Enrollment</p>
                <p className="text-3xl font-bold text-foreground mt-1">{snap?.networkEnrollmentPct ?? 0}%</p>
              </div>
              <CheckCircle className="w-5 h-5 text-green-400 dark:text-green-500 mt-1" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert Strip */}
      {alerts.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Active Alerts</p>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {alerts.map((alert: any, i: number) => (
              <AlertChip key={i} alert={alert} />
            ))}
          </div>
        </div>
      )}

      {/* Adoption Distribution + License Pool */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-muted-foreground">Adoption Distribution</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { label: "Building (0–40)", key: "brokersBuilding", color: "bg-gray-400", total: snap?.totalBrokers || 1 },
              { label: "Active (41–70)", key: "brokersActive", color: "bg-blue-500", total: snap?.totalBrokers || 1 },
              { label: "Power User (71–100)", key: "brokersPowerUser", color: "bg-green-500", total: snap?.totalBrokers || 1 },
            ].map((band) => {
              const count = snap?.[band.key] ?? 0;
              const pct = snap?.totalBrokers ? Math.round((count / snap.totalBrokers) * 100) : 0;
              return (
                <div key={band.key}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-muted-foreground">{band.label}</span>
                    <span className="text-sm font-semibold">{count} brokers</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className={`${band.color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-muted-foreground">License Pool</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Purchased</span>
              <span className="font-semibold">{totalLicenses}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Assigned</span>
              <span className="font-semibold text-primary">{snap?.licensesAssigned ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Available</span>
              <span className="font-semibold text-green-600 dark:text-green-400">{snap?.licensesAvailable ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Self-Funded (graduated)</span>
              <span className="font-semibold text-muted-foreground">{snap?.licensesSelfFunded ?? 0}</span>
            </div>
            <div className="pt-2">
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{ width: totalLicenses ? `${Math.round(((snap?.licensesAssigned ?? 0) / totalLicenses) * 100)}%` : "0%" }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {totalLicenses ? Math.round(((snap?.licensesAssigned ?? 0) / totalLicenses) * 100) : 0}% utilization
              </p>
            </div>
            <Link href="/ga/license-pool">
              <Button variant="outline" size="sm" className="w-full mt-1">
                Manage License Pool <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Broker Network List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-sm font-semibold text-muted-foreground">Broker Network</CardTitle>
          <Link href="/ga/brokers">
            <Button variant="ghost" size="sm" className="text-xs text-primary">
              View All <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {brokers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No broker accounts yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground border-b border-border">
                    <th className="text-left pb-2">Agency</th>
                    <th className="text-left pb-2">Score</th>
                    <th className="text-left pb-2">Band</th>
                    <th className="text-left pb-2">Last Login</th>
                    <th className="text-left pb-2">Employers</th>
                    <th className="text-left pb-2">Enrollment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {brokers.slice(0, 8).map((b: any) => (
                    <tr key={b.brokerAccountId} className="hover:bg-muted/50">
                      <td className="py-2.5">
                        <Link href={`/ga/brokers/${b.brokerAccountId}`}>
                          <span className="text-primary hover:underline cursor-pointer font-medium">
                            {b.brokerAgencyName || "—"}
                          </span>
                        </Link>
                      </td>
                      <td className="py-2.5">
                        <span className={`font-bold ${
                          (b.adoptionScore || 0) >= 71 ? "text-green-600 dark:text-green-400" :
                          (b.adoptionScore || 0) >= 41 ? "text-primary" : "text-muted-foreground"
                        }`}>
                          {b.adoptionScore ?? 0}
                        </span>
                      </td>
                      <td className="py-2.5"><AdoptionBadge band={b.adoptionBand || "building"} /></td>
                      <td className="py-2.5 text-muted-foreground">
                        {b.lastLoginAt ? new Date(b.lastLoginAt).toLocaleDateString() : "Never"}
                      </td>
                      <td className="py-2.5">{b.employersTotal ?? 0}</td>
                      <td className="py-2.5">{b.enrollmentCompletionPct ?? 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-muted-foreground">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {recentActivity.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No recent activity</p>
          ) : (
            <div className="space-y-2">
              {recentActivity.map((entry: any) => (
                <div key={entry.id} className="flex items-center gap-3 text-sm">
                  <span className="text-muted-foreground text-xs whitespace-nowrap">
                    {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : ""}
                  </span>
                  <span className="text-foreground">{entry.action}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
